import { useState, useCallback, useEffect } from 'react';

export function useFullscreen() {
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  // CSS-based immersive fallback for browsers without the Fullscreen API (e.g. iOS Safari)
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState(false);

  const nativeSupported =
    typeof document !== 'undefined' &&
    typeof document.documentElement.requestFullscreen === 'function';

  const enterFullscreen = useCallback(async (element?: HTMLElement) => {
    const target = element || document.documentElement;
    if (nativeSupported && target.requestFullscreen) {
      try {
        await target.requestFullscreen();
        return;
      } catch {
        // fall through to CSS fallback
      }
    }
    setIsFallbackFullscreen(true);
  }, [nativeSupported]);

  const exitFullscreen = useCallback(async () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        await document.exitFullscreen();
      } catch {
        // ignore
      }
    }
    setIsFallbackFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(async (element?: HTMLElement) => {
    if (document.fullscreenElement || isFallbackFullscreen) {
      await exitFullscreen();
    } else {
      await enterFullscreen(element);
    }
  }, [enterFullscreen, exitFullscreen, isFallbackFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsNativeFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return {
    isFullscreen: isNativeFullscreen || isFallbackFullscreen,
    isFallbackFullscreen,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
}
